<template>
    <div v-if="showPrice && price && currency" class="price-filter">
        <v-expansion-panels v-model="expanded" style="background-color: transparent">
            <v-expansion-panel expand>
                <v-expansion-panel-title class="v-subheader theme--light">
                    {{ labelText }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <v-row no-gutters class="price-filter__items mb-5">
                        <v-col>
                            <v-text-field
                                v-model="minPriceModel"
                                :type="minPriceFocused ? 'number' : 'text'"
                                single-line
                                variant="outlined"
                                hide-details
                                density="compact"
                                bg-color="white"
                                @focus="minPriceFocus"
                                @blur="setMinPrice"
                                @update:model-value="minPriceInput"
                            />
                        </v-col>
                        <v-col>
                            <v-text-field
                                v-model="maxPriceModel"
                                :type="maxPriceFocused ? 'number' : 'text'"
                                single-line
                                variant="outlined"
                                hide-details
                                density="compact"
                                bg-color="white"
                                @focus="maxPriceFocus"
                                @blur="setMaxPrice"
                                @update:model-value="maxPriceInput"
                            />
                        </v-col>
                    </v-row>
                    <v-range-slider
                        v-model="sliderPrice"
                        strict
                        :min="min"
                        hide-details
                        :max="max"
                        :thumb-label="false"
                        :disabled="disabled"
                        @start="start"
                        @end="end"
                    />
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
        <v-divider />
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch, Emit} from 'vue-facing-decorator'

    @Component({})
    export class PriceFilter extends Vue {
        @Prop() filters
        @Prop({default: null}) label
        @Prop({default: null}) nights
        @Prop({required: true}) currency
        @Prop({required: true}) filterValues

        sliderPrice = [0, 0]
        sliderStart = false
        expanded = 0
        minPriceFocusedValue = null
        minPriceFocused = false
        maxPriceFocusedValue = null
        maxPriceFocused = false

        @Emit()
        change(value: number[]) {
            return {key: 'price', value}
        }

        @Watch('filterValues.price', {deep: true, immediate: true})
        onChangeFilterValues(val: number[]) {
            if (!this.sliderStart && val[0] !== Infinity) this.sliderPrice = val
        }

        start() {
            this.sliderStart = true
        }

        end() {
            this.change(this.sliderPrice)
            this.sliderStart = false
        }

        minPriceFocus() {
            this.minPriceFocusedValue = null
            this.minPriceFocused = true
        }

        minPriceInput(val) {
            this.minPriceFocused = true
            this.minPriceFocusedValue = val
        }

        get minPriceModel() {
            return this.minPriceFocused
                ? this.minPriceFocusedValue
                : this.$filters.price({amount: this.price[0], currency: this.currency})
        }

        set minPriceModel(value) {
            if (this.minPriceFocused) {
                this.minPriceFocusedValue = value
            }
        }

        get maxPriceModel() {
            return this.maxPriceFocused
                ? this.maxPriceFocusedValue
                : this.$filters.price({amount: this.price[1], currency: this.currency})
        }

        set maxPriceModel(value) {
            if (this.maxPriceFocused) {
                this.maxPriceFocusedValue = value
            }
        }

        setMinPrice() {
            const minPriceFocusedValue = +parseFloat(this.minPriceFocusedValue).toFixed(2)
            if (minPriceFocusedValue >= this.min && minPriceFocusedValue <= this.price[1]) {
                const price1Converted = this.$filters.convertPrice(
                    {amount: minPriceFocusedValue, currency: this.selectedCurrency},
                    this.currency
                ).amount

                const price2Converted = this.$filters.convertPrice(
                    {amount: this.price[1], currency: this.selectedCurrency},
                    this.currency
                ).amount

                this.change([price1Converted, price2Converted])
            }
            this.minPriceFocused = false
        }

        maxPriceFocus() {
            this.maxPriceFocusedValue = null
            this.maxPriceFocused = true
        }

        setMaxPrice() {
            const maxPriceFocusedValue = +parseFloat(this.maxPriceFocusedValue).toFixed(2)
            if (maxPriceFocusedValue <= this.max && maxPriceFocusedValue >= this.price[0]) {
                const price1Converted = this.$filters.convertPrice(
                    {amount: this.price[0], currency: this.selectedCurrency},
                    this.currency
                ).amount

                const price2Converted = this.$filters.convertPrice(
                    {amount: maxPriceFocusedValue, currency: this.selectedCurrency},
                    this.currency
                ).amount

                this.change([price1Converted, price2Converted])
            }
            this.maxPriceFocused = false
        }

        get price() {
            return this.sliderPrice
        }

        set price(value) {
            this.sliderPrice = value
        }

        get min() {
            return Math.floor(this.filters['price'][0])
        }

        get max() {
            return Math.ceil(this.filters['price'][1])
        }

        get selectedCurrency() {
            return persistentStore.currency !== 'ORIGINAL' ? persistentStore.currency : this.currency
        }

        get labelText() {
            return (
                this.label ||
                `${this.$t('price_for')}  ${this.nights} ${this.$t('night', this.nights)} ${this.$t('in')} ${
                    this.selectedCurrency
                }`
            )
        }

        get showPrice() {
            return true
        }

        get disabled() {
            return this.max === Infinity
        }
    }

    export default PriceFilter
</script>

<style scoped lang="scss">
    :deep(.v-expansion-panel-content__wrap) {
        padding: 0;
    }
</style>
